// src/pages/Dashboard.tsx - Updated with Firebase Storage

import React, { useState, useEffect, useRef } from 'react';
import { Card, Upload, Button, Input, message, Progress, Steps, Tag, Modal, Grid, Skeleton, Alert, Space, Select } from 'antd';
import {
  InboxOutlined,
  PlayCircleOutlined,
  FileTextOutlined,
  CheckCircleOutlined,
  LoadingOutlined,
  ExclamationCircleOutlined,
  PauseCircleOutlined,
  DeleteOutlined,
  FilePdfOutlined,
  DownloadOutlined,
  ShareAltOutlined
} from '@ant-design/icons';
import { useGetIdentity } from '@refinedev/core';
import { doc, onSnapshot, setDoc, serverTimestamp } from 'firebase/firestore';
import { ref, uploadBytesResumable, getDownloadURL, deleteObject } from 'firebase/storage';
import { db, storage, auth } from '../utils/firebase';
import { getCurrentUserToken, clearImpersonationCache, authenticatedFetch } from '../providers/authProvider';
import { useNavigate } from 'react-router-dom';
import VideoPlayer from '../components/VideoPlayer';
import { ShareModal } from '../components/ShareModal';
import { PDFViewerModal } from '../components/pdf-viewer';
import { hasActivePlan } from '../utils/planLimits';

const { Dragger } = Upload;
const { TextArea } = Input;
const { Step } = Steps;

interface TaskStatus {
  status: 'PENDING' | 'PROCESSING' | 'RENDERING' | 'COMPLETED' | 'APPROVED' | 'ERROR';
  script_text?: string;
  error?: string;
  video_url?: string;
  report_data?: any;
  processed_data?: any;
}

interface JobData {
  job_id: string;
  status: string;
  script_review_enabled: boolean;
  generation_type?: 'script_only' | 'video';
  original_filename?: string;
  created_at?: any;
  tasks: {
    create_script: TaskStatus;
    extract_images: TaskStatus;
    create_narration: TaskStatus;
    create_video: TaskStatus;
  };
}

interface UploadTask {
  task: any; // Firebase upload task
  progress: number;
  state: 'running' | 'paused' | 'success' | 'canceled' | 'error';
  error?: string;
}

type TaskKey = 'create_script' | 'extract_images' | 'create_narration' | 'create_video';

const Dashboard: React.FC = () => {
  const [activeTab, setActiveTab] = useState('video');
  const [prompt, setPrompt] = useState('');
  const [uploading, setUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [currentJob, setCurrentJob] = useState<JobData | null>(null);
  const [scriptReviewEnabled, setScriptReviewEnabled] = useState(false);
  const [extraInstructions, setExtraInstructions] = useState('');
  const [language, setLanguage] = useState('en');
  const [showScriptModal, setShowScriptModal] = useState(false);
  const [editableScript, setEditableScript] = useState('');
  const [skipVideoCreation, setSkipVideoCreation] = useState(false);
  const [file, setFile] = useState<File | null>(null);
  const [uploadTask, setUploadTask] = useState<UploadTask | null>(null);
  const { data: user, refetch } = useGetIdentity<any>();
  
  const [isMobile, setIsMobile] = useState(() => window.innerWidth < 768);
  const [isDashboardReady, setIsDashboardReady] = useState(false);
  const [activeTaskKey, setActiveTaskKey] = useState<TaskKey | null>(null);
  const [shareModalVisible, setShareModalVisible] = useState(false);
  const [pdfViewerState, setPdfViewerState] = useState<{
    visible: boolean;
    pdfUrl: string;
    documentId: string;
    documentTitle: string;
  }>({ visible: false, pdfUrl: '', documentId: '', documentTitle: '' });
  const videoCompleteRef = useRef<HTMLDivElement>(null);

  const taskRefs = useRef<{ [key in TaskKey]: React.RefObject<HTMLDivElement> }>({
    create_script: React.createRef<HTMLDivElement>(),
    extract_images: React.createRef<HTMLDivElement>(),
    create_narration: React.createRef<HTMLDivElement>(),
    create_video: React.createRef<HTMLDivElement>(),
  });

  const navigate = useNavigate();

  useEffect(() => {
    const timer = setTimeout(() => {
      setIsDashboardReady(true);
    }, 50);

    return () => clearTimeout(timer);
  }, []); 

  useEffect(() => {
    const checkMobile = () => {
      setIsMobile(window.innerWidth < 768);
    };

    window.addEventListener('resize', checkMobile);
    return () => window.removeEventListener('resize', checkMobile);
  }, []);

  useEffect(() => {
    if (user?.language) {
      setLanguage(user.language);
    }
  }, [user]);

  useEffect(() => {
    if (activeTaskKey && taskRefs.current[activeTaskKey]?.current) {
      const timeoutId = setTimeout(() => {
        const element = taskRefs.current[activeTaskKey].current;
        if (element) {
          console.log('Scrolling to:', activeTaskKey);
          element.scrollIntoView({ 
            behavior: 'smooth', 
            block: 'center',
            inline: 'nearest' 
          });
          
          element.style.transition = 'background-color 0.3s';
          element.style.backgroundColor = '#e6f7ff';
          setTimeout(() => {
            element.style.backgroundColor = '';
          }, 2000);
        }
      }, 300);
  
      return () => clearTimeout(timeoutId);
    }
  }, [activeTaskKey]);

  const TASKS: { [key in TaskKey]: { name: string; order: number; description: string; } } = {
    'create_script': {
      name: 'Crafting Review',
      order: 1,
      description: 'Analyzing and summarizing inspection report'
    },
    'extract_images': { 
      name: 'Extracting Images', 
      order: 2, 
      description: 'Processing images from the report and creating structured content' 
    },
    'create_narration': { 
      name: 'Generating Narration', 
      order: 3, 
      description: 'Recording review as audio narration' 
    },
    'create_video': { 
      name: 'Creating Video', 
      order: 4, 
      description: 'Assembling final video presentation' 
    }
  };

  useEffect(() => {
    if (!currentJob?.job_id) return;

    const unsubscribe = onSnapshot(
      doc(db, 'jobs', currentJob.job_id),
      (doc) => {
        if (doc.exists()) {
          const data = doc.data() as JobData;
          setCurrentJob(prev => ({ ...prev, ...data }));

          if (data.script_review_enabled && 
              data.tasks.create_script.status === 'COMPLETED' && 
              data.tasks.create_script.script_text &&
              !showScriptModal) {
            setEditableScript(data.tasks.create_script.script_text);
            setShowScriptModal(true);
          }

          if (data.status === 'COMPLETED' && data.tasks.create_video.video_url) {
            message.success('Video generation completed!');
            clearImpersonationCache();
            refetch();
            setPrompt('');
            setExtraInstructions('');
            setFile(null);

            setTimeout(() => {
              videoCompleteRef.current?.scrollIntoView({ 
                behavior: 'smooth', 
                block: 'center' 
              });
            }, 500);
          }
          
          if (data.status === 'ERROR' || data.tasks.create_script.status === 'ERROR') {
            clearImpersonationCache();
            refetch();
            setPrompt('');
            setExtraInstructions('');
            setFile(null);
          }
        }
      },
      (error) => {
        console.error('Error listening to job updates:', error);
      }
    );

    return () => unsubscribe();
  }, [currentJob?.job_id, showScriptModal, refetch]);

  useEffect(() => {
    if (!currentJob) {
      setActiveTaskKey(null);
      return;
    }

    const taskOrder: TaskKey[] = ['create_script', 'extract_images', 'create_narration', 'create_video'];
    let newActiveKey: TaskKey | null = null;

    for (const key of taskOrder) {
      const task = currentJob.tasks[key];
      if (task?.status === 'PROCESSING' || task?.status === 'RENDERING') {
        newActiveKey = key;
        break;
      }
    }

    if (!newActiveKey) {
      for (const key of taskOrder) {
        const task = currentJob.tasks[key];
        if (task?.status === 'ERROR') {
          newActiveKey = key;
          break;
        }
      }
    }

    setActiveTaskKey(newActiveKey);
  }, [currentJob]);

  const getMaxFileSize = () => {
    return 50 * 1024 * 1024; // 50MB
  };

  const getMaxFileSizeMB = () => {
    return 50;
  };

  const handleFirebaseUpload = async (file: File, type: 'script' | 'video') => {
    // Check file size
    const maxSize = getMaxFileSize();
    if (file.size > maxSize) {
      message.error(`File too large. Maximum size: ${getMaxFileSizeMB()}MB`);
      return;
    }

    setUploading(true);
    setUploadProgress(0);

    try {
      // Generate unique job ID
      const jobId = `job_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
      
      // Create storage reference
      const timestamp = new Date().toISOString().replace(/[:.]/g, '-');
 
      // Check for impersonation first
      const impersonateUid = localStorage.getItem('impersonateUid');
      const userId = impersonateUid || user?.uid || user?.id || auth.currentUser?.uid;

      if (!userId) {
        message.error('User not authenticated');
        setUploading(false);
        return;
      }
      const storageRef = ref(storage, `uploads/${userId}/${timestamp}_${file.name}`);
            
      // Start resumable upload
      const uploadTaskInstance = uploadBytesResumable(storageRef, file, {
        contentType: 'application/pdf',
        customMetadata: {
          userId: user?.uid || '',
          jobId: jobId,
          originalName: file.name,
          generationType: type,
          uploadedAt: new Date().toISOString()
        }
      });

      setUploadTask({
        task: uploadTaskInstance,
        progress: 0,
        state: 'running'
      });

      // Monitor upload progress
      uploadTaskInstance.on('state_changed',
        (snapshot) => {
          const progress = (snapshot.bytesTransferred / snapshot.totalBytes) * 100;
          setUploadProgress(progress);
          setUploadTask(prev => prev ? { ...prev, progress } : null);
          
          switch (snapshot.state) {
            case 'paused':
              setUploadTask(prev => prev ? { ...prev, state: 'paused' } : null);
              break;
            case 'running':
              setUploadTask(prev => prev ? { ...prev, state: 'running' } : null);
              break;
          }
        },
        (error) => {
          console.error('Upload error:', error);
          setUploadTask(prev => prev ? { ...prev, state: 'error', error: error.message } : null);
          setUploading(false);
          
          switch (error.code) {
            case 'storage/unauthorized':
              message.error('Unauthorized. Please log in again.');
              break;
            case 'storage/canceled':
              message.info('Upload canceled');
              break;
            default:
              message.error('Upload failed: ' + error.message);
          }
        },
        async () => {
          // Upload completed successfully
          const downloadURL = await getDownloadURL(uploadTaskInstance.snapshot.ref);
          
          // Create job in Firestore
          const jobRef = doc(db, 'jobs', jobId);
          
          const jobData: any = {
            uid: userId,
            planType: user?.plan || user?.planType || 'free',
            status: 'PENDING',
            created_at: serverTimestamp(),
            storage_path: uploadTaskInstance.snapshot.ref.fullPath,
            file_url: downloadURL,
            original_filename: file.name,
            script_review_enabled: scriptReviewEnabled,
            generation_type: type === 'script' ? 'script_only' : 'video',
            skip_video_creation: skipVideoCreation,
            extra_instructions: extraInstructions,
            tasks: {
              create_script: { status: 'PENDING' },
              extract_images: { status: 'PENDING' },
              create_narration: { status: 'PENDING' },
              create_video: { status: 'PENDING' }
            }
          };
          
          // Paid plans get custom branding; free plans fall back to SpectReviews default
          const isPaidPlan = user?.plan && user.plan !== 'free';
          if (isPaidPlan && user?.companyLogo) {
            jobData.company_logo = user.companyLogo;
          }
          if (isPaidPlan && user?.company) {
            jobData.company_name = user.company;
          }
          
          await setDoc(jobRef, jobData);

          // Trigger backend processing
          const token = await getCurrentUserToken();
          const response = await fetch(`${process.env.REACT_APP_API_URL || 'http://localhost:8080'}/process-upload`, {
            method: 'POST',
            headers: {
              'Authorization': `Bearer ${token}`,
              'Content-Type': 'application/json'
            },
            body: JSON.stringify({
              jobId,
              storageUrl: downloadURL,
              storagePath: uploadTaskInstance.snapshot.ref.fullPath,
              generationType: type,
              extraInstructions,
              scriptReviewEnabled,
              language
            })
          });

          if (response.ok) {
            setCurrentJob({
              job_id: jobId,
              status: 'PROCESSING',
              script_review_enabled: scriptReviewEnabled,
              generation_type: type === 'script' ? 'script_only' : 'video',
              original_filename: file.name,
              tasks: {
                create_script: { status: 'PROCESSING' },
                extract_images: { status: 'PENDING' },
                create_narration: { status: 'PENDING' },
                create_video: { status: 'PENDING' }
              }
            });

            clearImpersonationCache();
            refetch();

            setUploadTask(prev => prev ? { ...prev, state: 'success' } : null);
            message.success('File uploaded successfully!');
          } else {
            const errBody = await response.json().catch(() => ({} as any));
            if (errBody?.error_code === 'subscription_required') {
              message.error({
                content: 'An active subscription is required — choose a plan to continue.',
                duration: 6,
              });
              navigate('/billing');
              return;
            }
            throw new Error(errBody?.error || 'Failed to start processing');
          }
          
          setUploading(false);
          setUploadProgress(0);
          setFile(null);
          setUploadTask(null);
        }
      );
    } catch (error) {
      console.error('Upload error:', error);
      message.error('Upload failed');
      setUploading(false);
      setUploadProgress(0);
    }
  };

  const handlePauseResume = () => {
    if (!uploadTask?.task) return;
    
    if (uploadTask.state === 'paused') {
      uploadTask.task.resume();
    } else if (uploadTask.state === 'running') {
      uploadTask.task.pause();
    }
  };

  const handleCancelUpload = () => {
    if (!uploadTask?.task) return;
    
    uploadTask.task.cancel();
    setUploadTask(null);
    setUploading(false);
    setUploadProgress(0);
  };

  const handleScriptApproval = async () => {
    if (!currentJob?.job_id) return;
    
    try {
      const token = await getCurrentUserToken();
      
      if (!token) {
        message.error('Authentication required. Please log in again.');
        return;
      }
      
      const impersonateUid = localStorage.getItem('impersonateUid');
      const body: any = {
        job_id: currentJob.job_id,
        script_text: editableScript
      };
      
      if (impersonateUid) {
        body.impersonate = impersonateUid;
      }
      
      const response = await fetch(`${process.env.REACT_APP_API_URL || 'http://localhost:8080'}/approve-script`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`,
        },
        body: JSON.stringify(body),
      });

      if (response.ok) {
        message.success('Script approved! Continuing with video generation...');
        setShowScriptModal(false);
      } else {
        const result = await response.json();
        message.error(result.error || 'Failed to approve script');
      }
    } catch (error) {
      console.error('Script approval error:', error);
      message.error('Failed to approve script');
    }
  };

  const handleScriptCancel = async () => {
    if (!currentJob?.job_id) return;
    
    try {
      const token = await getCurrentUserToken();
      
      if (!token) {
        message.error('Authentication required. Please log in again.');
        return;
      }
      
      const impersonateUid = localStorage.getItem('impersonateUid');
      const body: any = {
        job_id: currentJob.job_id,
        reason: 'User cancelled during script review'
      };
      
      if (impersonateUid) {
        body.impersonate = impersonateUid;
      }
      
      const response = await fetch(`${process.env.REACT_APP_API_URL || 'http://localhost:8080'}/cancel-job`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`,
        },
        body: JSON.stringify(body),
      });

      if (response.ok) {
        message.info('Job cancelled');
        setShowScriptModal(false);
        setCurrentJob(null);
      } else {
        setShowScriptModal(false);
        setCurrentJob(null);
        message.info('Job cancelled');
      }
    } catch (error) {
      setShowScriptModal(false);
      setCurrentJob(null);
      message.info('Job cancelled');
    }
  };

  // Helper to get display title for completed job
  const getDisplayTitle = (): string => {
    if (!currentJob) return 'Inspection Video';
    if ((currentJob as any).extracted_address) {
      return (currentJob as any).extracted_address;
    }
    if (currentJob.original_filename) {
      return currentJob.original_filename.replace('.pdf', '');
    }
    return `Video-${currentJob.job_id.substring(0, 8)}`;
  };

  // Handler to view PDF with annotations
  const handleViewPDF = async () => {
    if (!currentJob) return;

    try {
      // Fetch PDF as blob through authenticated proxy to avoid CORS issues
      const response = await authenticatedFetch(`/proxy-pdf/${currentJob.job_id}`);

      if (!response.ok) {
        const error = await response.json().catch(() => ({ error: 'Failed to load PDF' }));
        throw new Error(error.error || 'Failed to load PDF');
      }

      const blob = await response.blob();
      const blobUrl = URL.createObjectURL(blob);

      setPdfViewerState({
        visible: true,
        pdfUrl: blobUrl,
        documentId: currentJob.job_id,
        documentTitle: getDisplayTitle(),
      });
    } catch (error: any) {
      console.error('Error getting PDF:', error);
      message.error(error.message || 'Failed to load PDF');
    }
  };

  // Handler to download video
  const handleDownloadVideo = () => {
    if (!currentJob) return;

    const videoUrl = currentJob.tasks?.create_video?.video_url;
    if (videoUrl) {
      const link = document.createElement('a');
      link.href = videoUrl;
      link.download = `${currentJob.original_filename?.replace('.pdf', '')}_video.mp4` || 'inspection_video.mp4';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      message.success('Video download started!');
    } else {
      message.error('Video not available for download');
    }
  };

  // Handler to open share modal
  const handleShare = () => {
    if (!currentJob) return;

    const videoUrl = currentJob.tasks?.create_video?.video_url;
    if (!videoUrl) {
      message.error('Video is not ready to share yet');
      return;
    }
    setShareModalVisible(true);
  };

  const getStepStatus = (taskKey: TaskKey) => {
    if (!currentJob) return 'wait';
    const task = currentJob.tasks[taskKey];
    if (!task) return 'wait';
    switch (task.status) {
      case 'COMPLETED':
      case 'APPROVED': return 'finish';
      case 'PROCESSING':
      case 'RENDERING': return 'process';
      case 'ERROR': return 'error';
      default: return 'wait';
    }
  };

  const getStepIcon = (taskKey: TaskKey) => {
    const status = getStepStatus(taskKey);
    switch (status) {
      case 'finish': return <CheckCircleOutlined />;
      case 'process': return <LoadingOutlined />;
      case 'error': return <ExclamationCircleOutlined />;
      default: return null;
    }
  };

  const getCurrentStep = () => {
    if (!currentJob) return -1;
    const taskOrder: TaskKey[] = ['create_script', 'extract_images', 'create_narration', 'create_video'];
    for (let i = taskOrder.length - 1; i >= 0; i--) {
      const task = currentJob.tasks[taskOrder[i]];
      if (task?.status === 'COMPLETED' || task?.status === 'APPROVED') return i + 1;
      if (task?.status === 'PROCESSING' || task?.status === 'RENDERING') return i;
    }
    return 0;
  };

  const isJobProcessing = () => {
    return currentJob && (currentJob.status === 'PROCESSING' || currentJob.status === 'PENDING');
  };

  const uploadProps = {
    name: 'file',
    multiple: false,
    accept: '.pdf',
    disabled: isJobProcessing(),
    customRequest: ({ onSuccess }: any) => {
      onSuccess("ok");
    },
    beforeUpload: (uploadedFile: File) => {
      if (isJobProcessing()) {
        message.warning('Please wait for the current job to complete');
        return false;
      }
      
      // Check file size
      const maxSize = getMaxFileSize();
      if (uploadedFile.size > maxSize) {
        message.error(`File too large. Maximum size: ${getMaxFileSizeMB()}MB`);
        return false;
      }
      
      setFile(uploadedFile);
      return false;
    },
    onChange: (info: any) => {
      if (info.file.status === 'removed') {
        setFile(null);
      }
    },
    showUploadList: false,
  };
  
  if (!isDashboardReady) {
    return (
        <div style={{ maxWidth: '1200px', margin: '0 auto', padding: isMobile ? '12px' : '24px' }}>
            <Card>
                <Skeleton active paragraph={{ rows: 4 }} />
                <Skeleton active paragraph={{ rows: 6 }} style={{ marginTop: 24 }}/>
            </Card>
        </div>
    );
  }

  return (
    <div style={{ maxWidth: '1200px', margin: '0 auto', padding: isMobile ? '12px' : '24px' }}>
      <Card
        title={
          <span style={{ fontSize: isMobile ? '18px' : '20px' }}>
            New Inspection Review
          </span>
        }
        className="mb-6"
        bodyStyle={{ padding: (isJobProcessing() || currentJob?.status === 'COMPLETED') ? '0' : (isMobile ? '12px' : '24px'), display: (isJobProcessing() || currentJob?.status === 'COMPLETED') ? 'none' : 'block' }}
      >
            <div className="space-y-4">
              <p className="text-gray-600" style={{ fontSize: isMobile ? '14px' : '16px' }}>
                Create inspection review with video summary and shareable workspace.
              </p>
              
              {!hasActivePlan(user?.plan) ? (
                <Alert
                  message="Subscription Required"
                  description="Choose a plan to start creating reviews. Your first month's base is free."
                  type="warning"
                  showIcon
                  action={
                    <Button type="primary" size="small" onClick={() => navigate('/billing')}>
                      Choose a Plan
                    </Button>
                  }
                />
              ) : (
                <>
                  {!file && !uploadTask ? (
                    <Dragger 
                      {...uploadProps}
                      style={{ padding: isMobile ? '20px' : '40px' }}
                      disabled={isJobProcessing() || !hasActivePlan(user?.plan)}
                    >
                      <p className="ant-upload-drag-icon">
                        <InboxOutlined style={{ fontSize: isMobile ? '48px' : '64px' }} />
                      </p>
                      <p className="ant-upload-text" style={{ fontSize: isMobile ? '14px' : '16px' }}>
                        {isJobProcessing() ? 'Processing in progress...' : 'Click or drag PDF inspection report to upload'}
                      </p>
                      <p className="ant-upload-hint" style={{ fontSize: isMobile ? '12px' : '14px' }}>
                        {isJobProcessing() ? 'Please wait for the current job to complete' : `Maximum file size: ${getMaxFileSizeMB()}MB`}
                      </p>
                    </Dragger>
                  ) : uploadTask ? (
                    <div style={{ 
                      padding: '24px',
                      border: '2px dashed #d9d9d9',
                      borderRadius: '8px',
                      backgroundColor: '#fafafa',
                      textAlign: 'center'
                    }}>
                      <InboxOutlined style={{ fontSize: 48, color: '#1890ff', marginBottom: 16 }} />
                      <p style={{ fontSize: 16, fontWeight: 500, marginBottom: 8 }}>
                        {uploadTask.state === 'paused' ? 'Upload Paused' : 'Uploading...'} {file?.name}
                      </p>
                      <Progress 
                        percent={Math.round(uploadTask.progress)} 
                        status={uploadTask.state === 'error' ? 'exception' : 'active'}
                      />
                      <Space style={{ marginTop: 16 }}>
                        <Button 
                          icon={uploadTask.state === 'paused' ? <PlayCircleOutlined /> : <PauseCircleOutlined />}
                          onClick={handlePauseResume}
                        >
                          {uploadTask.state === 'paused' ? 'Resume' : 'Pause'}
                        </Button>
                        <Button 
                          danger 
                          icon={<DeleteOutlined />}
                          onClick={handleCancelUpload}
                        >
                          Cancel
                        </Button>
                      </Space>
                      {uploadTask.error && (
                        <p style={{ color: '#ff4d4f', marginTop: 8 }}>{uploadTask.error}</p>
                      )}
                    </div>
                  ) : (
                    <div style={{ 
                      padding: '24px',
                      border: '2px dashed #d9d9d9',
                      borderRadius: '8px',
                      backgroundColor: '#fafafa',
                      textAlign: 'center'
                    }}>
                      <FileTextOutlined style={{ fontSize: 48, color: '#1890ff', marginBottom: 16 }} />
                      <p style={{ fontSize: 16, fontWeight: 500, marginBottom: 8 }}>
                        {isJobProcessing() ? 'File processing:' : 'File ready:'} {file?.name}
                      </p>
                      {!isJobProcessing() && (
                        <>
                          <p style={{ color: '#666', marginBottom: 16 }}>
                            Click "Create Inspection Review" below to continue
                          </p>
                          <Button 
                            danger 
                            size="small" 
                            onClick={() => setFile(null)}
                          >
                            Remove File
                          </Button>
                        </>
                      )}
                    </div>
                  )}
                  
                  <div className="flex flex-col gap-3 p-4 bg-gray-50 rounded" style={{ marginTop: '20px' }}>
                    <label className="flex items-start gap-2" style={{ fontSize: isMobile ? '14px' : '16px' }}>
                      <input
                        type="checkbox"
                        style={{ marginRight: '5px', marginTop: '4px', flexShrink: 0 }}
                        checked={scriptReviewEnabled}
                        onChange={(e) => setScriptReviewEnabled(e.target.checked)}
                      />
                      <span style={{ lineHeight: 1.5 }}>Review and edit script before video generation</span>
                    </label>
                    <br/><br/>
                    <div style={{ marginBottom: '12px' }}>
                      <label style={{ display: 'block', marginBottom: '4px', fontSize: '14px', fontWeight: 500 }}>
                        Language
                      </label>
                      <Select
                        value={language}
                        onChange={(value) => setLanguage(value)}
                        style={{ width: '200px' }}
                      >
                        <Select.Option value="en">English</Select.Option>
                        <Select.Option value="es">Spanish</Select.Option>
                      </Select>
                    </div>
                    <TextArea
                      value={extraInstructions}
                      onChange={(e) => setExtraInstructions(e.target.value)}
                      placeholder="Extra instructions for the AI..."
                      rows={2}
                    />
                  </div>
                  
                  <Button 
                    type="primary" 
                    style={{ marginTop: '20px' }}
                    onClick={() => file && handleFirebaseUpload(file, 'video')}
                    loading={uploading}
                    disabled={!file || !hasActivePlan(user?.plan)}
                    size={isMobile ? 'middle' : 'large'}
                    block={isMobile}
                  >
                    Create Inspection Review
                  </Button>
                </>
              )}
            </div>
      </Card>

      {currentJob && currentJob.status !== 'COMPLETED' && (
        <Card
          title="Processing Status"
          className="mb-6"
          bodyStyle={{ padding: isMobile ? '12px' : '24px' }}
        >
          <div>
            {Object.entries(TASKS).map(([taskKey, task], index) => {
              const key = taskKey as TaskKey;
              if (currentJob.generation_type === 'script_only' &&
                  ['extract_images', 'create_narration', 'create_video'].includes(key)) {
                return null;
              }

              const taskStatus = currentJob.tasks[key];
              const status = getStepStatus(key);

              const isVideoTaskSkipped = key === 'create_video' && skipVideoCreation;

              return (
                <div
                  key={key}
                  ref={taskRefs.current[key]}
                  style={{
                    display: 'flex',
                    alignItems: 'flex-start',
                    padding: '20px',
                    marginBottom: '12px',
                    border: '1px solid #f0f0f0',
                    borderRadius: '8px',
                    backgroundColor: isVideoTaskSkipped ? '#f5f5f5' : '#fafafa',
                    width: '100%',
                    boxSizing: 'border-box',
                    position: 'relative',
                    borderLeft: `4px solid ${
                      isVideoTaskSkipped ? '#999' :
                      status === 'finish' ? '#52c41a' :
                      status === 'process' ? '#1890ff' :
                      status === 'error' ? '#ff4d4f' : '#d9d9d9'
                    }`
                  }}
                >
                  <div style={{
                    width: '32px',
                    height: '32px',
                    borderRadius: '50%',
                    backgroundColor: isVideoTaskSkipped ? '#999' :
                                    status === 'finish' ? '#52c41a' :
                                    status === 'process' ? '#1890ff' :
                                    status === 'error' ? '#ff4d4f' : '#f0f0f0',
                    color: status === 'wait' && !isVideoTaskSkipped ? '#999' : '#fff',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    fontWeight: 600,
                    fontSize: '16px',
                    marginRight: '16px',
                    flexShrink: 0
                  }}>
                    {isVideoTaskSkipped ? '—' :
                     status === 'finish' ? '✓' :
                     status === 'process' ? <LoadingOutlined spin /> :
                     status === 'error' ? '!' : (index + 1)}
                  </div>

                  <div style={{ flex: 1 }}>
                    <div style={{
                      fontWeight: 600,
                      fontSize: '16px',
                      marginBottom: '6px',
                      color: isVideoTaskSkipped ? '#999' : '#1f2937'
                    }}>
                      {task.name}
                    </div>
                    <div style={{
                      fontSize: '14px',
                      color: isVideoTaskSkipped ? '#999' : '#6b7280',
                      marginBottom: '8px'
                    }}>
                      {isVideoTaskSkipped ? 'Skipped in testing mode' : task.description}
                    </div>
                    {taskStatus?.error && !isVideoTaskSkipped && (
                      <div style={{
                        color: '#ff4d4f',
                        fontSize: '13px',
                        marginTop: '8px',
                        backgroundColor: '#fff2f0',
                        border: '1px solid #ffccc7',
                        padding: '8px 12px',
                        borderRadius: '4px'
                      }}>
                        Error: {taskStatus.error}
                      </div>
                    )}
                  </div>

                  <Tag
                    color={
                      isVideoTaskSkipped ? 'default' :
                      taskStatus?.status === 'COMPLETED' || taskStatus?.status === 'APPROVED' ? 'success' :
                      taskStatus?.status === 'PROCESSING' || taskStatus?.status === 'RENDERING' ? 'processing' :
                      taskStatus?.status === 'ERROR' ? 'error' : 'default'
                    }
                    style={{
                      margin: 0,
                      fontSize: '13px',
                      padding: '6px 16px',
                      fontWeight: 500
                    }}
                  >
                    {isVideoTaskSkipped ? 'SKIPPED' : (taskStatus?.status || 'PENDING')}
                  </Tag>
                </div>
              );
            })}
          </div>

          {currentJob && currentJob.generation_type === 'script_only' &&
           currentJob.tasks.create_script.status === 'COMPLETED' && (
            <div className="mt-6 p-4 bg-green-50 border border-green-200 rounded">
              <h3 className="text-lg font-semibold text-green-800 mb-2">
                ✓ Text Report Ready!
              </h3>
              <p className="text-green-700 mb-3">
                Your inspection text report has been generated.
              </p>
              <Button
                type="primary"
                onClick={() => navigate(`/scripts?view=${currentJob.job_id}`)}
                block={isMobile}
              >
                View Text Report
              </Button>
            </div>
          )}

          {skipVideoCreation && currentJob.tasks.create_narration.status === 'COMPLETED' && (
            <div className="mt-6 p-4 bg-blue-50 border border-blue-200 rounded">
              <h3 className="text-lg font-semibold text-blue-800 mb-2">
                Testing Complete!
              </h3>
              <p className="text-blue-700">
                Processing stopped after narration as requested. Video creation was skipped.
              </p>
            </div>
          )}
        </Card>
      )}

      {currentJob && currentJob.status === 'COMPLETED' && currentJob.tasks.create_video.video_url && (
        <Card
          ref={videoCompleteRef}
          title="Inspection Review Ready!"
          className="mb-6"
          bodyStyle={{ padding: isMobile ? '12px' : '24px' }}
        >
          <VideoPlayer
            videoUrl={currentJob.tasks.create_video.video_url}
            title={currentJob.original_filename || 'Inspection Video'}
            createdAt={currentJob.created_at}
            jobId={currentJob.job_id}
            hideTitle={true}
            onShare={handleShare}
          />

          {/* Action buttons card */}
          <Card
            size="small"
            style={{ marginTop: 24 }}
            bodyStyle={{ padding: isMobile ? '12px' : '16px' }}
          >
            <div style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              flexWrap: 'wrap',
              gap: '12px'
            }}>
              <div style={{ flex: '1 1 auto', minWidth: '200px' }}>
                <div style={{ fontWeight: 600, fontSize: '16px' }}>
                  {getDisplayTitle()}
                </div>
                <div style={{ fontSize: '12px', color: '#666', marginTop: '4px' }}>
                  {currentJob.created_at?.seconds
                    ? new Date(currentJob.created_at.seconds * 1000).toLocaleDateString() + ' at ' +
                      new Date(currentJob.created_at.seconds * 1000).toLocaleTimeString()
                    : new Date(currentJob.created_at).toLocaleDateString()}
                </div>
              </div>
              <div style={{
                display: 'flex',
                flexWrap: 'wrap',
                gap: '8px',
                justifyContent: isMobile ? 'stretch' : 'flex-end'
              }}>
                <Button
                  icon={<FilePdfOutlined />}
                  onClick={handleViewPDF}
                  size={isMobile ? 'middle' : 'middle'}
                  style={isMobile ? { flex: '1 1 45%' } : undefined}
                >
                  Review Report
                </Button>
                <Button
                  icon={<DownloadOutlined />}
                  onClick={handleDownloadVideo}
                  size={isMobile ? 'middle' : 'middle'}
                  style={isMobile ? { flex: '1 1 45%' } : undefined}
                >
                  Download Video
                </Button>
              </div>
            </div>
          </Card>
        </Card>
      )}

      <Modal
        title="Review Generated Script"
        open={showScriptModal}
        onCancel={handleScriptCancel}
        width={isMobile ? '95%' : 800}
        style={{ top: isMobile ? 20 : undefined }}
        footer={[
          <Button key="cancel" onClick={handleScriptCancel}>
            Cancel
          </Button>,
          <Button key="approve" type="primary" onClick={handleScriptApproval}>
            Approve Script and Continue
          </Button>,
        ]}
      >
        <div className="mb-4">
          <p className="text-gray-600 mb-2" style={{ fontSize: isMobile ? '14px' : '16px' }}>
            Review and edit the script below, then click approve to continue processing.
          </p>
          <p className="text-sm text-orange-600">
            Note: Do not change or add section headers or alter bracketed sections.
          </p>
        </div>
        
        <TextArea
          value={editableScript}
          onChange={(e) => setEditableScript(e.target.value)}
          rows={isMobile ? 10 : 15}
          className="font-mono text-sm"
        />
      </Modal>

      {/* PDF Viewer Modal */}
      <PDFViewerModal
        visible={pdfViewerState.visible}
        pdfUrl={pdfViewerState.pdfUrl}
        documentId={pdfViewerState.documentId}
        documentTitle={pdfViewerState.documentTitle}
        onClose={() => setPdfViewerState({ visible: false, pdfUrl: '', documentId: '', documentTitle: '' })}
      />

      {/* Share Modal */}
      {currentJob && (
        <ShareModal
          visible={shareModalVisible}
          onClose={() => setShareModalVisible(false)}
          jobId={currentJob.job_id}
          videoTitle={getDisplayTitle()}
        />
      )}
    </div>
  );
};

export default Dashboard;